/**
 * 요금제 데이터 액세스 계층
 * API 호출 로직을 캡슐화
 */

import { API_BASE_URL } from '../../../config/api';
import type {
  PricingPlan,
  SeatZone,
  ZonePricing,
  OperatingHours,
  PricingCreateRequest,
  PricingUpdateRequest,
} from '../models';

export class PricingRepository {
  /**
   * 공통 API 호출 함수
   */
  private async apiCall<T>(url: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('accessToken') || 'demo-token';
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * 요금제 목록 조회
   */
  async getPricingPlans(): Promise<PricingPlan[]> {
    try {
      return await this.apiCall<PricingPlan[]>('/v1/pricing/plans');
    } catch (error) {
      console.error('요금제 로드 실패:', error);
      throw error;
    }
  }

  /**
   * 요금제 생성
   */
  async createPricingPlan(data: PricingCreateRequest): Promise<PricingPlan> {
    try {
      return await this.apiCall<PricingPlan>('/v1/pricing/plans', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('요금제 생성 실패:', error);
      throw error;
    }
  }

  /**
   * 요금제 수정
   */
  async updatePricingPlan(planId: string, data: PricingUpdateRequest): Promise<PricingPlan> {
    try {
      return await this.apiCall<PricingPlan>(`/v1/pricing/plans/${planId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('요금제 수정 실패:', error);
      throw error;
    }
  }

  /**
   * 요금제 삭제
   */
  async deletePricingPlan(planId: string): Promise<void> {
    try {
      await this.apiCall<void>(`/v1/pricing/plans/${planId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('요금제 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 구역 목록 조회
   */
  async getZones(): Promise<SeatZone[]> {
    try {
      return await this.apiCall<SeatZone[]>('/v1/seat-config/zones');
    } catch (error) {
      console.error('구역 로드 실패:', error);
      throw error;
    }
  }

  /**
   * 구역별 요금 조회
   */
  async getZonePricing(): Promise<ZonePricing[]> {
    try {
      return await this.apiCall<ZonePricing[]>('/v1/pricing/zone-pricing');
    } catch (error) {
      console.error('구역별 요금 로드 실패:', error);
      throw error;
    }
  }

  /**
   * 운영시간 조회
   */
  async getOperatingHours(): Promise<OperatingHours[]> {
    try {
      return await this.apiCall<OperatingHours[]>('/v1/pricing/operating-hours');
    } catch (error) {
      console.error('운영시간 로드 실패:', error);
      throw error;
    }
  }

  /**
   * 운영시간 업데이트
   */
  async updateOperatingHours(hours: OperatingHours[]): Promise<OperatingHours[]> {
    try {
      return await this.apiCall<OperatingHours[]>('/v1/pricing/operating-hours', {
        method: 'PUT',
        body: JSON.stringify(hours),
      });
    } catch (error) {
      console.error('운영시간 업데이트 실패:', error);
      throw error;
    }
  }
}

// 싱글톤 인스턴스
export const pricingRepository = new PricingRepository();
